import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { MessageThreadSortType, SearchFilter } from "gql/graphql";
import isArray from "lodash/isArray";

export const ORGANIZATION_ID_QUERY_STRING = "organizationId";
export const MESSAGE_ID_QUERY_STRING = "message";
export const MESSAGE_THREAD_ID_QUERY_STRING = "thread";
export const FILTER_QUERY_STRING = "filter";
export const SORT_QUERY_STRING = "sort";
export const SEARCH_HIGHLIGHTS_QUERY_STRING = "highlights";
export const MESSAGE_HIGHLIGHTS_QUERY_STRING = "message-highlights";
export const MESSAGE_SEARCH_QUERY_STRING = "message-search";
export const TIMESTAMP_QUERY_STRING = "_t";
export const SUCCESS_QUERY_STRING = "success";
export const TOKEN_QUERY_STRING = "token";
export const INVITE_ID_QUERY_STRING = "iid";

// decodeURIComponent throws (URIError) on malformed input such as a stray "%"
// (e.g. searching for "1%"). Fall back to the raw value so search never crashes.
const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const useQueryString = () => {
  const { query, isReady } = useRouter();

  const serializedQuery = JSON.stringify(query);
  const lastSerializedQuery = useRef(serializedQuery);
  const [stableQuery, setStableQuery] = useState(query);

  useEffect(() => {
    if (isReady && serializedQuery !== lastSerializedQuery.current) {
      lastSerializedQuery.current = serializedQuery;
      setStableQuery(query);
    }
  }, [isReady, serializedQuery, query]);

  const getStringArrayFromQueryString = useCallback(
    (queryString: string) => {
      const highlights = stableQuery[queryString] as string;
      if (highlights) {
        return JSON.parse(safeDecodeURIComponent(highlights)) as string[];
      }
      return [];
    },
    [stableQuery],
  );

  const organizationId = useMemo(
    () =>
      isReady
        ? (stableQuery[ORGANIZATION_ID_QUERY_STRING] as string | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const messageId = useMemo(
    () =>
      isReady
        ? (stableQuery[MESSAGE_ID_QUERY_STRING] as string | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const threadId = useMemo(
    () =>
      isReady
        ? (stableQuery[MESSAGE_THREAD_ID_QUERY_STRING] as string | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const inviteId = useMemo(
    () =>
      isReady
        ? (stableQuery[INVITE_ID_QUERY_STRING] as string | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const messageSearch = useMemo(
    () =>
      isReady ? stableQuery[MESSAGE_SEARCH_QUERY_STRING] === "true" : false,
    [stableQuery, isReady],
  );

  const timestamp = useMemo(
    () =>
      isReady
        ? (stableQuery[TIMESTAMP_QUERY_STRING] as string | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const isSuccess = useMemo(
    () => (isReady ? stableQuery[SUCCESS_QUERY_STRING] === "true" : false),
    [stableQuery, isReady],
  );
  const token = useMemo(
    () =>
      isReady
        ? (stableQuery[TOKEN_QUERY_STRING] as string | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const sortType = useMemo(
    () =>
      isReady
        ? (stableQuery[SORT_QUERY_STRING] as MessageThreadSortType | undefined)
        : undefined,
    [stableQuery, isReady],
  );

  const searchHighlights = useMemo(
    () =>
      isReady
        ? getStringArrayFromQueryString(SEARCH_HIGHLIGHTS_QUERY_STRING)
        : [],
    [getStringArrayFromQueryString, isReady],
  );

  const messageHighlights = useMemo(
    () =>
      isReady
        ? getStringArrayFromQueryString(MESSAGE_HIGHLIGHTS_QUERY_STRING)
        : [],
    [getStringArrayFromQueryString, isReady],
  );

  const convertFilterQueryToSearchFilters = useCallback(
    (filterQuery?: string | string[]): SearchFilter[] => {
      if (!filterQuery) {
        return [];
      }

      const jsonArray = isArray(filterQuery) ? filterQuery : [filterQuery];
      const filters: SearchFilter[] = jsonArray.map((json) => JSON.parse(json));
      return filters.map((filter) => ({
        ...filter,
        stringValue: filter?.stringValue
          ? safeDecodeURIComponent(filter.stringValue)
          : filter?.stringValue,
      }));
    },
    [],
  );

  const searchFilters = useMemo(
    () =>
      isReady
        ? convertFilterQueryToSearchFilters(stableQuery[FILTER_QUERY_STRING])
        : [],
    [stableQuery, isReady, convertFilterQueryToSearchFilters],
  );

  const getStringArrayQueryString = useCallback(
    (value: string[]) => encodeURIComponent(JSON.stringify(value)),
    [],
  );

  return {
    qsOrganizationId: organizationId,
    qsTimestamp: timestamp,
    qsMessageId: messageId,
    qsMessageThreadId: threadId,
    qsMessageSearch: messageSearch,
    qsSearchFilters: searchFilters,
    qsSortType: sortType,
    qsSearchHighlights: searchHighlights,
    qsMessageHighlights: messageHighlights,
    qsIsSuccess: isSuccess,
    qsToken: token,
    qsInviteId: inviteId,
    getStringArrayQueryString,
  };
};

export default useQueryString;
